/* eslint-disable ember/no-computed-properties-in-native-classes, ember/require-computed-property-dependencies */
import Model, {
  attr,
  belongsTo,
  type AsyncBelongsTo,
} from '@warp-drive/legacy/model';
import { computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import type { Type } from '@warp-drive/core/types/symbols';
import type { FragmentArray } from 'ember-data-model-fragments/array/fragment';
import { fragment, fragmentArray } from 'ember-data-model-fragments/attributes';
import type { EnumValue } from '@linc-technologies/linced-common/enums/base-enum';
import type DataType from '@linc-technologies/linced-common/enums/data-type';
import PermissionType from '@linc-technologies/linced-common/enums/events/permission-type';
import InputType from '@linc-technologies/linced-common/enums/input-type';
import type Value from '@linc-technologies/linced-common/models/dynamic-field/value';
import type FieldOption from '@linc-technologies/linced-common/models/people-schema/field-option';
import type School from '@linc-technologies/linced-common/models/school';

// Proto: events.Permission.
export default class Permission extends Model {
  declare [Type]: 'events/permission';

  // createTime is when the resource was created in seconds from the epoch.
  @attr('number')
  declare createTime: number | undefined;

  // updateTime is the last time the resource was modified in seconds from
  // the epoch.
  @attr('number')
  declare updateTime: number | undefined;

  // schoolId is the school the permission is assigned to.
  @belongsTo('school', { async: true, inverse: null })
  declare schoolId: AsyncBelongsTo<School>;

  @alias('schoolId')
  declare school: AsyncBelongsTo<School>;

  // type is the type of the permission.
  @attr('number', { defaultValue: 0 })
  declare type: EnumValue<typeof PermissionType> | 0;

  @attr('number', { defaultValue: 0 })
  declare inputType: EnumValue<typeof InputType> | 0;

  @attr('number', { defaultValue: 0 })
  declare dataType: EnumValue<typeof DataType> | 0;

  // label is a label of the permission
  @attr('string', { defaultValue: '' })
  declare label: string;

  // label is a label of the permission shown to caregivers
  @attr('string', { defaultValue: '' })
  declare caregiverLabel: string;

  @computed('id')
  get key() {
    if (this.id) {
      return this.id.replaceAll('-', ':');
    }
    return '';
  }

  @fragment('dynamic-field/value')
  declare default: Value | null;

  @attr('string', { defaultValue: '' })
  declare placeholder: string;

  @fragmentArray('people-schema/field-option')
  declare options: FragmentArray<FieldOption>;

  @attr('boolean', { defaultValue: false })
  declare inUse: boolean;

  @computed('type')
  get typeString() {
    if (this.type === PermissionType.PERMISSION) {
      return 'Permission';
    }
    return 'Option';
  }

  @computed('inputType')
  get inputTypeString() {
    return InputType.getLabel(this.inputType);
  }

  @computed('inputType', 'options.[]')
  get optionSummary() {
    switch (this.inputType) {
      case InputType.NUMBER:
      case InputType.SWITCH:
      case InputType.TEXTAREA:
        return this.inputTypeString;
      default:
        return this.options
          .map((option) => {
            return option.value;
          })
          .join('/');
    }
  }
}
